import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { DefaultLogger } from '@oliasoft/logger';
import { apiCallBegan, type ApiErrorPayload } from 'src/store/middleware/api/api';
import type { TAppDispatch } from 'src/store/store-types';
import {
  acknowledgeReleaseNotesLocally,
  cleanupOldAcknowledgedNotes,
  getAcknowledgedReleaseNotes,
  removeAcknowledgedReleaseNote,
} from 'views/navigation/new-app-version/release-notes-storage';
import type { ReleaseNote, TReleaseNotesState, TUnseenNotesResponse } from './release-notes.types';

const log = new DefaultLogger('releaseNote');
log.setLevel('INFO');

const initialState: TReleaseNotesState = {
  unseenNotes: [],
  isLoading: false,
  error: null,
  hasLoaded: false,
  newVersion: null,
  isNewVersionDetected: false,
};

const releaseNotes = createSlice({
  name: 'releaseNotes',
  initialState,
  reducers: {
    notesRequested: (state) => {
      state.isLoading = true;
      state.error = null;
      state.hasLoaded = false;
    },

    notesReceived: (state, action: PayloadAction<TUnseenNotesResponse>) => {
      const notes = action.payload?.notes ?? [];
      const overflowIds = action.payload?.overflowIds ?? [];

      // Notes beyond the cap are auto-acknowledged locally so they never pile up
      if (overflowIds.length > 0) {
        acknowledgeReleaseNotesLocally(overflowIds);
      }

      const acknowledgedIds = getAcknowledgedReleaseNotes();
      state.unseenNotes = notes.filter((note) => !acknowledgedIds.includes(note.id));

      state.isLoading = false;
      state.hasLoaded = true;

      cleanupOldAcknowledgedNotes();
    },

    notesRequestFailed: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.unseenNotes = [];
      state.hasLoaded = true;
    },

    noteAcknowledgedSuccess: (state, action: PayloadAction<{ releaseId: string }>) => {
      state.unseenNotes = state.unseenNotes.filter((note) => note.id !== action.payload.releaseId);
      removeAcknowledgedReleaseNote(action.payload.releaseId);
    },

    newVersionDetected: (state, action: PayloadAction<{ newVersion: string }>) => {
      state.newVersion = action.payload.newVersion;
      state.isNewVersionDetected = true;
    },

    /**
     * Clean up version detection state. Called after the user acknowledges
     * release notes.
     */
    versionAcknowledged: (state) => {
      state.isNewVersionDetected = false;
      state.newVersion = null;
    },
  },
});

export const {
  notesRequested,
  notesReceived,
  notesRequestFailed,
  noteAcknowledgedSuccess,
  newVersionDetected,
  versionAcknowledged,
} = releaseNotes.actions;

export default releaseNotes.reducer;

/**
 * Fetch all unseen release notes for the current user.
 * toast - the user can keep working if this fails.
 */
export const fetchUnseenReleaseNotes = () =>
  apiCallBegan({
    url: '/api/release-notes/unseen',
    method: 'POST',
    data: {},
    onStart: notesRequested.type,
    onSuccess: notesReceived.type,
    onError: (error: ApiErrorPayload) => {
      log.warn('Failed to fetch release notes:', error?.message);
      return notesRequestFailed(error?.message ?? 'Failed to fetch release notes');
    },
  });

/**
 * Acknowledge multiple release notes on the backend
 * This is our safety net so the same notes never pop up twice:
 * - We reload the page right after, so some of these requests may get
 *   cut off before they finish - that's fine.
 * - localStorage already remembers what the user saw (that's the source
 *   of truth), so the modal won't show again even if the backend missed it.
 * - syncLocalAckWithServer resends anything that didn't make it, the next
 *   time the app starts.
 */
export const acknowledgeAllReleaseNotes = (releaseIds: string[]) => async (dispatch: TAppDispatch) => {
  // Reset the new-version flag BEFORE removing notes. If we reset after the
  // loop, there is a window where unseenNotes is already empty but
  // isNewVersionDetected is still true, which makes the provider briefly show
  // the fallback NewAppVersionModal. Resetting first closes that window.
  dispatch(versionAcknowledged());
  for (const releaseId of releaseIds) {
    await dispatch(
      apiCallBegan({
        url: `/api/release-notes/${releaseId}/ack`,
        method: 'POST',
        data: {},
        onSuccess: () => noteAcknowledgedSuccess({ releaseId }),
        onError: (error: ApiErrorPayload) => {
          log.warn(`ACK failed for release note ${releaseId}:`, error?.message);
        },
      }),
    );
  }
};

/**
 * Attempt to sync locally-acknowledged notes with the server. Best-effort:
 * on failure it is retried on the next launch.
 */
export const syncLocalAckWithServer = (releaseIds: string[]) => async (dispatch: TAppDispatch) => {
  if (releaseIds.length === 0) return;

  log.info(`Syncing ${releaseIds.length} locally-acknowledged notes with server`);

  try {
    await dispatch(acknowledgeAllReleaseNotes(releaseIds));
  } catch (error) {
    log.warn('Failed to sync local acknowledgments, will retry on next launch:', error);
  }
};

export type { ReleaseNote };
